<template>
  <modal-view
    @close-modal="close"
    :title="`Sale Details #${saleCart.local_sale_cart_id}`"
    customMaxWidth="900px"
    customContentHeight="auto"
  >
    <div class="space-y-6">
      <!-- Savings Highlight -->
      <div
        class="bg-gradient-to-r from-success/20 to-success/10 border-2 border-success rounded-lg p-6"
      >
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600 dark:text-gray-400 mb-1">
              Total Savings
            </p>
            <p class="text-4xl font-bold text-success">
              {{ formatCurrency(totalSavings) }}
            </p>
            <p class="text-sm text-gray-600 dark:text-gray-400 mt-2">
              with promo code
              <span
                class="px-2 py-1 rounded bg-blue-100 text-blue-700 dark:bg-blue-600 dark:text-white font-semibold ml-1"
              >
                {{ promoCode }}
              </span>
            </p>
          </div>
          <div class="text-right">
            <p class="text-sm text-gray-600 dark:text-gray-400 mb-1">
              Original Total
            </p>
            <p class="text-2xl font-semibold line-through text-gray-500">
              {{ formatCurrency(originalTotal) }}
            </p>
            <p class="text-sm text-gray-600 dark:text-gray-400 mt-2">
              Final Total
            </p>
            <p class="text-2xl font-bold text-gray-800 dark:text-white">
              {{ formatCurrency(finalTotal) }}
            </p>
          </div>
        </div>
      </div>

      <!-- Sale Information -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="space-y-3">
          <h3
            class="text-lg font-semibold text-gray-800 dark:text-white border-b pb-2"
          >
            Sale Information
          </h3>
          <div class="info-row">
            <span class="label">Sale ID:</span>
            <span class="value">#{{ saleCart.local_sale_cart_id }}</span>
          </div>
          <div class="info-row">
            <span class="label">Status:</span>
            <span class="value">
              <span :class="getStatusClass(saleCart.status)">
                {{ saleCart.status }}
              </span>
            </span>
          </div>
          <div class="info-row">
            <span class="label">Invoice:</span>
            <span class="value">#{{ saleCart.invoice_number }}</span>
          </div>
          <div class="info-row">
            <span class="label">Date:</span>
            <span class="value">{{ formatDate(saleCart.created_at) }}</span>
          </div>
          <div class="info-row">
            <span class="label">Recorded By:</span>
            <span class="value">{{
              saleCart.recorded_by?.names || "N/A"
            }}</span>
          </div>
          <div class="info-row" v-if="saleCart.payment_plan?.customer">
            <span class="label">Customer:</span>
            <span class="value">{{
              saleCart.payment_plan.customer.names || "Walk-in"
            }}</span>
          </div>
          <div class="info-row" v-if="saleCart.note">
            <span class="label">Note:</span>
            <span class="value">{{ saleCart.note }}</span>
          </div>
        </div>

        <!-- Payment Plan Information -->
        <div class="space-y-3" v-if="saleCart.payment_plan">
          <h3
            class="text-lg font-semibold text-gray-800 dark:text-white border-b pb-2"
          >
            Payment Details
          </h3>
          <div class="info-row">
            <span class="label">Promo Code:</span>
            <span
              class="px-2 py-1 rounded bg-blue-100 text-blue-700 dark:bg-blue-600 dark:text-white font-semibold text-sm"
            >
              {{ promoCode }}
            </span>
          </div>
          <div class="info-row">
            <span class="label">Discount Type:</span>
            <span class="value">{{ discountType }}</span>
          </div>
          <div class="info-row">
            <span class="label">Discount Amount:</span>
            <span class="value text-success font-bold">
              -{{ formatCurrency(totalSavings) }}
            </span>
          </div>
          <div class="info-row">
            <span class="label">Final Amount:</span>
            <span class="value font-bold">
              {{ formatCurrency(finalTotal) }}
            </span>
          </div>
          <div
            class="info-row"
            v-if="saleCart.payment_plan?.installments?.length > 0"
          >
            <span class="label">Installments:</span>
            <span class="value">{{
              saleCart.payment_plan.installments.length
            }}</span>
          </div>
          <div class="info-row" v-if="totalPaid > 0">
            <span class="label">Total Paid:</span>
            <span class="value text-success font-bold">
              {{ formatCurrency(totalPaid) }}
            </span>
          </div>
        </div>
      </div>

      <operational-unit-assignment
        v-if="resolvedStoreId && saleCart?.id"
        :store-id="resolvedStoreId"
        target-type="sale-cart"
        :target-id="saleCart.id"
        :model-value="assignedOperationalUnit"
        :editable="assignmentEditable"
        :correction="isFinalized"
        @assigned="$emit('sale-cart-updated', $event)"
      />

      <!-- Installment Details -->
      <div v-if="saleCart.payment_plan?.installments?.length > 0">
        <h3
          class="text-lg font-semibold text-gray-800 dark:text-white border-b pb-2 mb-3"
        >
          Payment Installments
        </h3>
        <div class="space-y-2">
          <div
            v-for="(installment, index) in saleCart.payment_plan.installments"
            :key="installment.id"
            class="p-3 bg-gray-50 dark:bg-slate-700 rounded"
          >
            <div class="flex justify-between items-center mb-2">
              <span class="font-medium text-gray-900 dark:text-white">
                Installment #{{ index + 1 }}
              </span>
              <span
                class="px-2 py-1 text-xs font-medium rounded-full"
                :class="
                  installment.status === 'PAID'
                    ? 'bg-success/20 text-success'
                    : 'bg-gray-200 text-gray-700'
                "
              >
                {{ installment.status }}
              </span>
            </div>
            <div class="grid grid-cols-2 gap-2 text-sm">
              <div class="text-gray-600 dark:text-gray-400">Expected:</div>
              <div class="font-medium text-gray-900 dark:text-white">
                {{ formatCurrency(installment.expected_payment_amount) }}
              </div>
              <div class="text-gray-600 dark:text-gray-400">Paid:</div>
              <div class="font-medium text-success">
                {{ formatCurrency(installment.amount_paid || 0) }}
              </div>
              <div class="text-gray-600 dark:text-gray-400">Due Date:</div>
              <div class="font-medium text-gray-900 dark:text-white">
                {{ formatDate(installment.expected_payment_date) }}
              </div>
              <div
                v-if="installment.paid_at"
                class="text-gray-600 dark:text-gray-400"
              >
                Paid At:
              </div>
              <div
                v-if="installment.paid_at"
                class="font-medium text-gray-900 dark:text-white"
              >
                {{ formatDate(installment.paid_at) }}
              </div>
            </div>
            <!-- Payment Account Info -->
            <div
              v-if="installment.payment_plan_installment_payments?.length > 0"
              class="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600"
            >
              <div
                v-for="payment in installment.payment_plan_installment_payments"
                :key="payment.id"
                class="text-xs text-gray-600 dark:text-gray-400"
              >
                Paid via:
                <span class="font-medium">{{
                  payment.payment_account?.name || "Unknown"
                }}</span>
                - {{ formatCurrency(payment.amount) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Items List -->
      <div v-if="saleCart.product_sales || saleCart.service_sales">
        <h3
          class="text-lg font-semibold text-gray-800 dark:text-white border-b pb-2 mb-3"
        >
          Items
        </h3>

        <!-- Products -->
        <div
          v-if="saleCart.product_sales && saleCart.product_sales.length > 0"
          class="mb-4"
        >
          <p class="font-medium text-gray-700 dark:text-gray-300 mb-2">
            Products
          </p>
          <div class="space-y-2">
            <div
              v-for="product in saleCart.product_sales"
              :key="product.id"
              class="flex justify-between items-center p-3 bg-gray-50 dark:bg-slate-700 rounded"
            >
              <div>
                <p class="font-medium text-gray-900 dark:text-white">
                  {{ product.product?.name || "Product" }}
                </p>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  Qty: {{ product.quantity }} ×
                  {{
                    formatCurrency(product.product_sale_price?.unit_price || 0)
                  }}
                </p>
              </div>
              <p class="font-semibold text-gray-900 dark:text-white">
                {{
                  formatCurrency(
                    (product.quantity || 0) *
                      (product.product_sale_price?.unit_price || 0)
                  )
                }}
              </p>
            </div>
          </div>
        </div>

        <!-- Services -->
        <div v-if="saleCart.service_sales && saleCart.service_sales.length > 0">
          <p class="font-medium text-gray-700 dark:text-gray-300 mb-2">
            Services
          </p>
          <div class="space-y-2">
            <div
              v-for="service in saleCart.service_sales"
              :key="service.id"
              class="flex justify-between items-center p-3 bg-gray-50 dark:bg-slate-700 rounded"
            >
              <div>
                <p class="font-medium text-gray-900 dark:text-white">
                  {{ service.service?.name || "Service" }}
                </p>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  Qty: {{ service.quantity }} ×
                  {{ formatCurrency(service.sale_price || 0) }}
                </p>
              </div>
              <p class="font-semibold text-gray-900 dark:text-white">
                {{
                  formatCurrency(
                    (service.quantity || 0) * (service.sale_price || 0)
                  )
                }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </modal-view>
</template>

<script>
import ModalView from "@/components/modals/ModalView.vue";
import OperationalUnitAssignment from "@/components/operational-units/OperationalUnitAssignment.vue";

export default {
  name: "SaleCartDetailsModal",
  components: {
    ModalView,
    OperationalUnitAssignment,
  },
  props: {
    saleCart: {
      type: Object,
      required: true,
    },
    storeId: {
      type: [String, Number],
      default: null,
    },
  },
  emits: ["close", "sale-cart-updated"],
  computed: {
    resolvedStoreId() {
      return this.saleCart.store_id || this.saleCart.store?.id || this.storeId || null;
    },
    assignedOperationalUnit() {
      if (this.isFinalized && this.saleCart.operational_unit_snapshot) {
        const snapshot = this.saleCart.operational_unit_snapshot;
        return {
          id: snapshot.unit_id || this.saleCart.operational_unit_id,
          code: snapshot.unit_code,
          name: snapshot.unit_name,
          operational_unit_type: snapshot.unit_type_id
            ? {
                id: snapshot.unit_type_id,
                code: snapshot.unit_type_code,
                singular_label: snapshot.unit_type_label,
              }
            : null,
          path: snapshot.path || [],
          snapshot: true,
        };
      }
      return (
        this.saleCart.operational_unit ||
        this.saleCart.store_operational_unit ||
        (this.saleCart.operational_unit_id
          ? { id: this.saleCart.operational_unit_id }
          : null)
      );
    },
    assignmentEditable() {
      if (this.isFinalized) return this.canCorrectFinalizedAttribution;
      return this.canWriteSaleCart && (!this.saleCart.ticket_cart_id || this.canWriteTicket);
    },
    isFinalized() {
      return ["PAID", "COMPLETED", "FINALIZED", "REFUNDED", "CANCELLED"].includes(
        String(this.saleCart.status || "").toUpperCase(),
      );
    },
    selectedStore() {
      if (this.saleCart.store) return this.saleCart.store;
      const resolver = this.$store?.getters?.getStoreById;
      return typeof resolver === "function" ? resolver(this.resolvedStoreId) : null;
    },
    canCorrectFinalizedAttribution() {
      if (this.selectedStore?.isOwner) return true;
      return (this.selectedStore?.permissions || []).some(
        (permission) =>
          String(permission || "").trim().toUpperCase() === "CAN_CHANGE_STORE_SETTINGS",
      );
    },
    canWriteSaleCart() {
      if (this.selectedStore?.isOwner) return true;
      const permissions = new Set(
        (this.selectedStore?.permissions || []).map((permission) =>
          String(permission || "").trim().toUpperCase(),
        ),
      );
      return [
        "CAN_WRITE_SALE_CARTS",
        "CASHIER_CAN_WRITE_SALE_CARTS",
        "MANAGER_CAN_WRITE_SALE_CARTS",
      ].some((permission) => permissions.has(permission));
    },
    canWriteTicket() {
      if (this.selectedStore?.isOwner) return true;
      const permissions = new Set(
        (this.selectedStore?.permissions || []).map((permission) =>
          String(permission || "").trim().toUpperCase(),
        ),
      );
      return [
        "CAN_WRITE_TICKET_CARTS",
        "CASHIER_CAN_WRITE_TICKET_CARTS",
        "MANAGER_CAN_WRITE_TICKET_CARTS",
      ].some((permission) => permissions.has(permission));
    },
    promoCode() {
      const plan = this.saleCart.payment_plan;
      if (!plan) return "N/A";
      return plan.promo_code?.code || plan.group_promo_code?.code || "N/A";
    },
    discountType() {
      const plan = this.saleCart.payment_plan;
      if (!plan) return "N/A";
      const promo = plan.promo_code || plan.group_promo_code;
      if (!promo) return "N/A";

      if (promo.discount_percentage) {
        const percentage = parseFloat(promo.discount_percentage);
        return `${percentage}% off`;
      } else if (promo.discount_amount) {
        return `Fixed ${this.formatCurrency(promo.discount_amount)}`;
      }
      return "N/A";
    },
    totalSavings() {
      const plan = this.saleCart.payment_plan;
      if (!plan?.discount) return 0;
      return parseFloat(plan.discount.total_discount_amount || 0);
    },
    finalTotal() {
      const plan = this.saleCart.payment_plan;
      if (!plan) return 0;
      return parseFloat(plan.total_amount_after_discount || 0);
    },
    originalTotal() {
      return this.finalTotal + this.totalSavings;
    },
    totalPaid() {
      const plan = this.saleCart.payment_plan;
      if (!plan?.installments) return 0;
      return plan.installments.reduce(
        (sum, inst) => sum + parseFloat(inst.amount_paid || 0),
        0
      );
    },
  },
  methods: {
    close() {
      this.$emit("close");
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat("en-RW", {
        style: "currency",
        currency: "RWF",
        minimumFractionDigits: 0,
      }).format(amount);
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      return new Date(dateString).toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    getStatusClass(status) {
      const classes = {
        PAID: "px-2 py-1 rounded bg-success/20 text-success font-semibold",
        PAYMENT_INITIATED:
          "px-2 py-1 rounded bg-warning/20 text-warning font-semibold",
        PENDING: "px-2 py-1 rounded bg-gray-200 text-gray-700 font-semibold",
      };
      return classes[status] || "px-2 py-1 rounded bg-gray-200 text-gray-700";
    },
  },
};
</script>

<style scoped>
.info-row {
  @apply flex justify-between items-center;
}

.info-row .label {
  @apply text-sm text-gray-600 dark:text-gray-400;
}

.info-row .value {
  @apply text-sm font-medium text-gray-800 dark:text-white;
}
</style>
